import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  IconButton,
  useTheme,
  Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HistoryIcon from "@mui/icons-material/History";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppTablePagenation from "../../../../../views/components/common/pages/AppTablePagenation";
import {
  formatDate,
  formatDateWithoutTime,
} from "../../../../../views/components/common/functions/formatDate";
import { useStores } from "../../../use-stores";
import { ShipmentActivityLog } from "../../../../../models/Shipments/ShipmentActivityLog";
import InlineFormError from "../../../../../views/components/common/form/InlineFormError";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import {
  getModernPageColors,
  modernDrawerHeaderSx,
  modernEmptyStateSx,
} from "../../../../../styles/modernListingPageSx";

/** Above AppBar / edit dialog. */
const ACTIVITY_LOGS_DRAWER_Z_OFFSET = 2000;

const displayChangeValue = (
  value: string | number | boolean | null,
): string => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return String(value);
};

const groupLogsByDate = (
  logs: ShipmentActivityLog[],
): Record<string, ShipmentActivityLog[]> => {
  const grouped: Record<string, ShipmentActivityLog[]> = {};
  logs.forEach((log) => {
    const dateKey = formatDateWithoutTime(log.created_date, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
    if (!grouped[dateKey]) grouped[dateKey] = [];
    grouped[dateKey].push(log);
  });
  return grouped;
};

const getInitials = (name: string | null): string => {
  const trimmed = name?.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(" ").filter(Boolean);
  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return trimmed[0].toUpperCase();
};

const getAvatarColors = (theme: Theme): string[] => {
  const c = theme.palette.customColors;
  return [
    c.darkBlue,
    c.successGreen,
    c.errorRed,
    c.purple,
    c.pinkDark,
    c.blueGradientStart,
    c.EmeraldGreen,
    c.tealGradientStart,
  ];
};

const getAvatarColor = (theme: Theme, name: string | null): string => {
  const avatarColors = getAvatarColors(theme);
  const trimmed = name?.trim() || "?";
  let hash = 0;
  for (let i = 0; i < trimmed.length; i++) {
    hash = trimmed.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

const ActivityLogEntry: React.FC<{ log: ShipmentActivityLog }> = ({ log }) => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const [expanded, setExpanded] = useState(false);
  const hasChangeDetails = Boolean(log.change_details?.length);

  return (
    <Box sx={{ display: "flex", gap: 1.25 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: "50%",
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.7rem",
          fontWeight: 700,
          color: theme.palette.customColors.white,
          backgroundColor: getAvatarColor(theme, log.admin_name),
        }}
      >
        {getInitials(log.admin_name)}
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          border: `1px solid ${colors.border}`,
          borderRadius: 2,
          p: 1.5,
          bgcolor: colors.surface,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
            mb: 0.75,
          }}
        >
          <Chip
            label={log.action_label || log.action || "Activity"}
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: "0.7rem" }}
          />
          <CustomTypography
            variant="body2"
            sx={{ color: colors.subtitle, fontSize: "0.72rem" }}
          >
            {formatDate(log.created_date)}
          </CustomTypography>
        </Box>

        <CustomTypography
          variant="body2"
          sx={{ color: colors.title, lineHeight: 1.5, wordBreak: "break-word" }}
        >
          {log.summary}
        </CustomTypography>

        {(log.sku || log.pack_id != null || log.inventory_id != null) && (
          <CustomTypography
            variant="body2"
            sx={{
              mt: 0.75,
              color: colors.subtitle,
              fontSize: "0.72rem",
              wordBreak: "break-word",
            }}
          >
            {[
              log.sku ? `SKU ${log.sku}` : null,
              log.pack_id != null ? `Pack #${log.pack_id}` : null,
              log.inventory_id != null
                ? `Inventory #${log.inventory_id}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </CustomTypography>
        )}

        <CustomTypography
          variant="body2"
          sx={{
            mt: 0.5,
            color: colors.subtitle,
            fontSize: "0.72rem",
          }}
        >
          {log.admin_name?.trim() || "Unknown admin"}
        </CustomTypography>

        {hasChangeDetails && (
          <>
            <Box
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                mt: 1,
                cursor: "pointer",
                color: colors.accent,
                width: "fit-content",
              }}
            >
              <CustomTypography
                variant="body2"
                sx={{ fontSize: "0.72rem", fontWeight: 600, color: "inherit" }}
              >
                {expanded ? "Hide changes" : "View changes"}
              </CustomTypography>
              {expanded ? (
                <ExpandLessIcon sx={{ fontSize: 16 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16 }} />
              )}
            </Box>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  mt: 1,
                  borderRadius: 1.5,
                  bgcolor: colors.surfaceSoft,
                  p: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.5,
                }}
              >
                {log.change_details?.map((change, index) => (
                  <Box
                    key={`${log.id}-${change.field}-${index}`}
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: 0.5,
                      fontSize: "0.72rem",
                    }}
                  >
                    <CustomTypography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: colors.title,
                        fontSize: "0.72rem",
                      }}
                    >
                      {change.label}:
                    </CustomTypography>
                    <CustomTypography
                      variant="body2"
                      sx={{ color: colors.subtitle, fontSize: "0.72rem" }}
                    >
                      {displayChangeValue(change.from)} →{" "}
                      {displayChangeValue(change.to)}
                    </CustomTypography>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </>
        )}
      </Box>
    </Box>
  );
};

const ShipmentActivityLogsDrawer: React.FC = () => {
  const theme = useTheme<Theme>();
  const colors = getModernPageColors(theme);
  const { shipmentStore } = useStores();
  const shipmentId = shipmentStore.activityLogsShipmentId;
  const open = shipmentId != null;

  useEffect(() => {
    if (!open || shipmentId == null) return;
    void shipmentStore.getShipmentActivityLogs(shipmentId, 1).catch(() => {
      // Error is stored on shipmentStore.activityLogsError for inline display.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, shipmentId]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => {
    if (shipmentId == null) return;
    void shipmentStore.getShipmentActivityLogs(shipmentId, page).catch(() => {
      // Error is stored on shipmentStore.activityLogsError for inline display.
    });
  };

  const handleRetry = () => {
    if (shipmentId == null) return;
    void shipmentStore
      .getShipmentActivityLogs(shipmentId, shipmentStore.activityLogsPageNo)
      .catch(() => {
        // Error is stored on shipmentStore.activityLogsError for inline display.
      });
  };

  const groupedLogs = groupLogsByDate(shipmentStore.activityLogs);
  const sortedDates = Object.keys(groupedLogs).sort(
    (a, b) => new Date(b).getTime() - new Date(a).getTime(),
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => shipmentStore.closeActivityLogs()}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460, md: 520 },
          maxWidth: { xs: "100vw", sm: 520 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) =>
            muiTheme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + ACTIVITY_LOGS_DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      <Box sx={modernDrawerHeaderSx(theme)}>
        <Box display="flex" gap={1.5} alignItems="center" minWidth={0}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "10px",
              backgroundColor: theme.palette.primary.main,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <HistoryIcon sx={{ color: "white", fontSize: 22 }} />
          </Box>
          <Box minWidth={0}>
            <CustomTypography variant="heading5" sx={{ color: colors.title }}>
              Activity History
            </CustomTypography>
            <CustomTypography
              variant="body2"
              sx={{
                mt: 0.5,
                color: colors.subtitle,
                fontSize: { xs: "0.8rem", sm: "0.875rem" },
              }}
            >
              {shipmentId
                ? `Shipment #${shipmentId}`
                : "Shipment history"}
            </CustomTypography>
          </Box>
        </Box>

        <IconButton
          onClick={() => shipmentStore.closeActivityLogs()}
          size="small"
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: { xs: 1.5, sm: 2.5 },
          py: { xs: 1.5, sm: 2 },
        }}
      >
        {shipmentStore.activityLogsLoading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 240,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : shipmentStore.activityLogsError ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 1.5,
              minHeight: 240,
              pt: 1,
            }}
          >
            <InlineFormError message={shipmentStore.activityLogsError} />
            <AppButton
              label="Retry"
              variant="grayscale"
              onClick={handleRetry}
              customStyles={{ minWidth: "fit-content", height: 34 }}
            />
          </Box>
        ) : shipmentStore.activityLogs.length === 0 ? (
          <Box sx={{ ...modernEmptyStateSx(theme), minHeight: 240 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1.5,
              }}
            >
              <HistoryIcon sx={{ fontSize: 44, opacity: 0.5 }} />
              <CustomTypography variant="body1">
                No activity recorded for this shipment yet.
              </CustomTypography>
            </Box>
          </Box>
        ) : (
          sortedDates.map((date) => (
            <Box key={date} sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box
                  sx={{ height: "1px", flex: 1, backgroundColor: colors.border }}
                />
                <CustomTypography
                  variant="body2"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    fontWeight: 600,
                    fontSize: "0.72rem",
                    backgroundColor: colors.headerBg,
                    borderRadius: "12px",
                    color: colors.subtitle,
                  }}
                >
                  {date}
                </CustomTypography>
                <Box
                  sx={{ height: "1px", flex: 1, backgroundColor: colors.border }}
                />
              </Box>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {groupedLogs[date].map((log) => (
                  <ActivityLogEntry key={log.id} log={log} />
                ))}
              </Box>
            </Box>
          ))
        )}
      </Box>

      {shipmentStore.activityLogsTotalCount > 0 && (
        <Box sx={{ borderTop: `1px solid ${colors.border}` }}>
          <AppTablePagenation
            fontSize="0.75rem"
            pageNo={shipmentStore.activityLogsPageNo}
            limit={shipmentStore.activityLogsLimit}
            totalCount={shipmentStore.activityLogsTotalCount}
            showRowsPerPageOptions={false}
            onPageChange={handlePageChange}
            handleRowsChange={() => {}}
          />
        </Box>
      )}
    </Drawer>
  );
};

export default observer(ShipmentActivityLogsDrawer);
